import { isCompleteArchSelection, type PricingMode } from '../../../convex/lib/applicationType'

export type ClinicalSelectionIssueCode =
  | 'teeth_required'
  | 'teeth_required_with_implants'
  | 'arch_required'
  | 'implant_details_required'

export type ClinicalSelectionStatus = {
  valid: boolean
  code: 'complete' | ClinicalSelectionIssueCode
  title: string
  description: string
  actionMode?: 'teeth' | 'implants'
  missingImplantPositions: string[]
}

type ClinicalSelectionInput = {
  selectedTeeth: string[]
  selectedImplants: string[]
  implantTypes?: Record<string, string>
  pricingMode?: PricingMode
}

function sortPositions(positions: string[]): string[] {
  return [...positions].sort((a, b) => Number(a) - Number(b))
}

export function getMissingImplantTypePositions(
  selectedImplants: string[],
  implantTypes: Record<string, string> = {}
): string[] {
  return sortPositions(
    selectedImplants.filter((position) => !implantTypes[position]?.trim())
  )
}

function formatPositions(positions: string[]): string {
  if (positions.length === 1) return positions[0]
  return `${positions.slice(0, -1).join(', ')} e ${positions[positions.length - 1]}`
}

export function getClinicalSelectionStatus({
  selectedTeeth,
  selectedImplants,
  implantTypes = {},
  pricingMode,
}: ClinicalSelectionInput): ClinicalSelectionStatus {
  if (selectedTeeth.length === 0) {
    if (pricingMode === 'per_arch') {
      return {
        valid: false,
        code: 'arch_required',
        title: 'Manca l\u2019arcata da lavorare',
        description:
          'Seleziona l\u2019arcata superiore, quella inferiore oppure entrambe per continuare. Gli impianti sono informazioni aggiuntive e non sostituiscono l\u2019arcata.',
        actionMode: 'teeth',
        missingImplantPositions: [],
      }
    }

    if (selectedImplants.length > 0) {
      return {
        valid: false,
        code: 'teeth_required_with_implants',
        title: 'Hai selezionato solo impianti',
        description:
          'Seleziona almeno un dente per continuare: gli impianti sono informazioni aggiuntive e non possono sostituire i denti della prescrizione.',
        actionMode: 'teeth',
        missingImplantPositions: [],
      }
    }

    return {
      valid: false,
      code: 'teeth_required',
      title: 'Manca almeno un dente',
      description:
        'Selezionalo sull\u2019odontogramma per indicare dove applicare la prestazione. Gli impianti sono facoltativi.',
      actionMode: 'teeth',
      missingImplantPositions: [],
    }
  }

  if (pricingMode === 'per_arch' && !isCompleteArchSelection(selectedTeeth)) {
    return {
      valid: false,
      code: 'arch_required',
      title: 'Completa l\u2019arcata da lavorare',
      description:
        'Questa prestazione richiede un\u2019arcata completa. Seleziona l\u2019arcata superiore, quella inferiore oppure entrambe.',
      actionMode: 'teeth',
      missingImplantPositions: [],
    }
  }

  const missingImplantPositions = getMissingImplantTypePositions(
    selectedImplants,
    implantTypes
  )

  if (missingImplantPositions.length > 0) {
    const plural = missingImplantPositions.length > 1
    return {
      valid: false,
      code: 'implant_details_required',
      title: plural
        ? 'Completa i dati degli impianti'
        : 'Completa i dati dell\u2019impianto',
      description: plural
        ? `Per gli impianti nelle posizioni ${formatPositions(missingImplantPositions)} mancano nome e marca. Completa i menu sotto l\u2019odontogramma per continuare.`
        : `Per l\u2019impianto in posizione ${missingImplantPositions[0]} mancano nome e marca. Selezionali nel menu sotto l\u2019odontogramma per continuare.`,
      actionMode: 'implants',
      missingImplantPositions,
    }
  }

  const toothLabel = selectedTeeth.length === 1 ? '1 dente' : `${selectedTeeth.length} denti`
  const implantLabel =
    selectedImplants.length === 1
      ? '1 impianto configurato'
      : `${selectedImplants.length} impianti configurati`

  return {
    valid: true,
    code: 'complete',
    title: 'Selezione clinica completa',
    description:
      selectedImplants.length > 0
        ? `Hai selezionato ${toothLabel} e ${implantLabel}. Puoi continuare.`
        : `Hai selezionato ${toothLabel}. Gli impianti sono facoltativi: puoi continuare.`,
    missingImplantPositions: [],
  }
}
